import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { Observable, map } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { GlobalFacade } from '@app/global.facade';
import { FinancialPartnersInfoVM, UserBenefitResponse } from '@shared/benefits/entities/benefits';
import { environment as ENV } from '@environment';
import { InstallmentType } from '@app/modules/ux/entities/installment-types';
import { TzDatesHelper } from '@ux/helpers/tz-dates.helper';


@Component({
  selector: 'app-benefits-installments',
  templateUrl: './benefits-installments.container.html',
  styleUrls: ['./benefits-installments.container.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class BenefitInstallmentsContainer {

  @Input() totalPrice: number;
  @Input() currencyCode: string;
  @Input() isDesktop: boolean = false;
  @Input() isHotel: boolean = false;
  @Input() isFlightChanges: boolean = false;
  @Input() isInternational: boolean;
  @Input() isAutoCarView: boolean;
  @Input() searchBy: SearchBy;
  @Input() productReference: number | string;
  @Input() isCMS: boolean = false;

  @Output() viewFinancialPartners: EventEmitter<FinancialPartnersInfoVM> = new EventEmitter<FinancialPartnersInfoVM>();

  public partnerType = ENV.countryModules?.installments.partner_type as InstallmentType;

  constructor(
    private readonly globalFacade: GlobalFacade,
    private readonly translateService: TranslocoService,
  ) { }

  get showInstallmentsForChanges(): boolean {
    return this.isFlightChanges ? this.totalPrice > 0 : true;
  }

  get showPartners(): boolean {
    return this.partnerType !== InstallmentType.NONE;
  }

  get partnerTitle(): string {
    switch (this.partnerType) {
      case InstallmentType.BANKS:
        return 'INSTALLMENTS_INFO.PARTNER_BANKS';
      case InstallmentType.CARDS:
        return 'INSTALLMENTS_INFO.PARTNER_CARDS';
      default:
        return null;
    }
  }

  get benefitInstallments$(): Observable<UserBenefitResponse> {
    return this.globalFacade.benefits$.pipe(
      map(benefits => benefits.find(benefit => (
        this.isValidProductReference(benefit.product_references) &&
          this.isValidTimePeriod(benefit.start_date, benefit.end_date) &&
          this.isValidMinPrice(benefit.installments_min_value) &&
          benefit.installments_max_amount > 0) ||
          (benefit.is_default && benefit.installments_max_amount > 0)
      )),
    )
  }

  get installmentValue$(): Observable<number> {
    return this.benefitInstallments$.pipe(
      map(benefit => benefit ? +this.totalPrice / benefit.installments_max_amount : null)
    )
  }

  get productText(): string {
    let product: string;
    if (this.searchBy === SearchBy.PACKAGES) {
      product = this.isHotel ? SearchBy.HOTELS : SearchBy.FLIGHTS;
    } else {
      product = this.searchBy;
    }
    return this.translateService.translate(`INSTALLMENTS_INFO.PRODUCTS.${product}`);
  }

  public viewPartners(maxAmount: number, event: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.viewFinancialPartners.emit({totalPrice: this.totalPrice, maxAmount})
  }

  private isValidMinPrice(minValue: number): boolean {
    return this.totalPrice > minValue;
  }

  private isValidProductReference(references: string[] | number[]): boolean {
    return references?.some(ref => {
      return ref === this.productReference;
    });
  }

  private isValidTimePeriod(startDate: string, endDate: string): boolean {
    return new TzDatesHelper().isTodayBetweenTwoDates(startDate, endDate);
  }

}
